import { useState, useEffect, useCallback } from 'react';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';

type EntityType = 'contact' | 'lead';
type HiddenMap = Record<string, string>; // key -> ISO timestamp when hidden

const STORAGE_PREFIX = 'inbox-hidden-conversations:';

function makeKey(entityType: EntityType, id: string) {
  return `${entityType}-${id}`;
}

function readFromStorage(storageKey: string): HiddenMap {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeToStorage(storageKey: string, map: HiddenMap) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(map));
  } catch (err) {
    console.warn('[useHiddenConversations] Failed to persist:', err);
  }
}

export function useHiddenConversations() {
  const { data: currentAppUser } = useCurrentAppUser();
  const appUserId = currentAppUser?.id || null;
  const storageKey = appUserId ? `${STORAGE_PREFIX}${appUserId}` : null;

  const [hiddenMap, setHiddenMap] = useState<HiddenMap>({});

  // Load from storage when user changes
  useEffect(() => {
    if (!storageKey) {
      setHiddenMap({});
      return;
    }
    setHiddenMap(readFromStorage(storageKey));
  }, [storageKey]);

  // Sync across tabs
  useEffect(() => {
    if (!storageKey) return;
    const handler = (e: StorageEvent) => {
      if (e.key === storageKey) {
        setHiddenMap(readFromStorage(storageKey));
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [storageKey]);

  const hide = useCallback((entityType: EntityType, id: string) => {
    if (!storageKey) return;
    setHiddenMap((prev) => {
      const next = { ...prev, [makeKey(entityType, id)]: new Date().toISOString() };
      writeToStorage(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const unhide = useCallback((entityType: EntityType, id: string) => {
    if (!storageKey) return;
    setHiddenMap((prev) => {
      const key = makeKey(entityType, id);
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      writeToStorage(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const isHidden = useCallback(
    (entityType: EntityType, id: string, lastMessageTime: string | null): boolean => {
      const hiddenAt = hiddenMap[makeKey(entityType, id)];
      if (!hiddenAt) return false;
      // If a newer message arrived after hide, unhide automatically
      if (lastMessageTime && new Date(lastMessageTime).getTime() > new Date(hiddenAt).getTime()) {
        return false;
      }
      return true;
    },
    [hiddenMap]
  );

  return { hide, unhide, isHidden };
}
